import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

// Service ID mapping for feature links
const featureServiceMap: Record<string, string> = {
  "Companionship & Conversation": "companionship",
  "Medication Reminders": "medication",
  "Light Housekeeping": "housekeeping",
  "Meal Preparation": "meal-prep",
  "Transportation Assistance": "transportation",
  "Personal Care (Bathing/Dressing)": "personal-care",
  "ADL Support (Mobility/Transferring)": "adl",
};

const plans = [
  {
    id: "essential",
    name: "Essential Care",
    tagline: "Perfect for light assistance and independent living",
    description:
      "Our Essential Care plan provides foundational support for clients who need occasional help while maintaining independence.",
    features: [
      "Companionship & Conversation",
      "Medication Reminders",
      "Light Housekeeping",
      "Meal Preparation",
      "Transportation Assistance",
      "Personal Care (Bathing/Dressing)",
      "ADL Support (Mobility/Transferring)",
      "Overnight Care",
    ],
    cta: "Speak with a Care Advisor",
    highlight: false,
  },
  {
    id: "complete",
    name: "Complete Care",
    tagline: "Ideal for consistent daily support",
    description:
      "Designed for clients who require regular assistance with personal care and daily living activities.",
    features: [
      "All Essential Care Services",
      "Personal Care (Bathing/Dressing)",
      "ADL Support (Mobility/Transferring)",
      "Daily Wellness Check-Ins",
      "Care Plan Adherence",
      "Overnight Care",
      "24/7 Live-in Care",
      "Specialized Dementia Support",
    ],
    cta: "Get Your Personalized Quote",
    highlight: true,
  },
  {
    id: "total",
    name: "Total Care",
    tagline: "Comprehensive, round-the-clock support",
    description:
      "Our highest level of care for clients needing continuous supervision and dedicated support.",
    features: [
      "All Complete Care Services",
      "Overnight Care",
      "24/7 Live-in Care Option",
      "Specialized Care Coordination",
      "Priority Caregiver Matching",
      "Family Portal Access",
      "Monthly Care Reviews",
      "Dementia & Parkinson's Support",
    ],
    cta: "Request a Consultation",
    highlight: false,
  },
];

const billingOptions = [
  {
    title: "Private Pay",
    description:
      "Flexible scheduling with convenient payment methods. Transparent billing with no hidden fees.",
    icon: "ri-wallet-3-line",
  },
  {
    title: "Medicaid Waivers",
    description:
      "We assist eligible clients with waiver programs and enrollment guidance.",
    icon: "ri-government-line",
  },
  {
    title: "Long-Term Care Insurance",
    description:
      "We coordinate directly with insurers and help with necessary documentation.",
    icon: "ri-shield-check-line",
  },
];

const steps = [
  { num: "01", title: "Contact Us", desc: "Call or fill out our quick form" },
  { num: "02", title: "Free Assessment", desc: "We evaluate your care needs at home" },
  { num: "03", title: "Care Plan Creation", desc: "We design a personalized plan" },
  { num: "04", title: "Care Begins", desc: "Matched caregiver starts support" },
];

const faqs = [
  {
    q: "Is there a minimum number of hours per visit?",
    a: "Yes, our minimum visit is typically 3 hours. This ensures your caregiver has enough time to provide quality care without feeling rushed.",
  },
  {
    q: "Can I change my care plan at any time?",
    a: "Absolutely. Care needs change, and so can your plan. Contact our care coordinators at any time to adjust hours, services, or caregivers.",
  },
  {
    q: "Are caregivers employees or independent contractors?",
    a: "All Sycamore Oasis caregivers are W-2 employees. This means they are fully insured, background-checked, trained, and supervised by our team.",
  },
  {
    q: "What if my regular caregiver is unavailable?",
    a: "We maintain a team of qualified backup caregivers for every client. You will never be left without coverage.",
  },
  {
    q: "Do you offer short-term or respite care?",
    a: "Yes, we offer flexible short-term care, respite care for family caregivers, and post-hospital recovery support on a temporary basis.",
  },
];

const ourPromise = [
  {
    title: "Compassionate Caregivers",
    desc: "Every team member is chosen for empathy, patience, and genuine dedication to improving lives.",
    icon: "ri-heart-3-line",
  },
  {
    title: "Personalized Matching",
    desc: "We carefully pair each client with a caregiver whose skills and personality fit their unique needs.",
    icon: "ri-user-heart-line",
  },
  {
    title: "Ongoing Care Coordination",
    desc: "Our care coordinators regularly check in, review progress, and adjust plans as needs evolve.",
    icon: "ri-calendar-check-line",
  },
  {
    title: "Fully Licensed & Insured",
    desc: "All caregivers are W-2 employees, background-checked, trained, and covered by our insurance.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Family Communication",
    desc: "We keep families informed with regular updates, open communication, and a dedicated portal for Total Care plans.",
    icon: "ri-chat-heart-line",
  },
  {
    title: "No Long-Term Contracts",
    desc: "Start, pause, or adjust care anytime. You stay in full control with no locked-in commitments.",
    icon: "ri-hand-heart-line",
  },
];

export default function CarePlans() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20green%20and%20beige%20organic%20shapes%20pattern%2C%20elegant%20background%20with%20warm%20neutral%20tones%20and%20emerald%20green%20accents%2C%20calming%20professional%20atmosphere%2C%20minimal%20elegant%20design%2C%20no%20text%2C%20gentle%20gradients&width=1600&height=500&seq=planhero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Your Care, Your Way
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Compassionate Care, Personalized for You
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Every family is unique. We take the time to understand your needs and design a care plan that feels like home — warm, flexible, and built around the people you love.
          </p>
        </div>
      </section>

      {/* Our Promise */}
      <section
        className="py-14 md:py-20 bg-white"
        data-animate-id="promise"
        ref={setRef("promise")}
      >
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("promise", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Why Families Trust Us
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Our Promise to You
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Choosing care for a loved one is a deeply personal decision. Here is what you can count on when you welcome us into your home.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ourPromise.map((item, i) => (
              <div
                key={item.title}
                className={`bg-soft-gray rounded-xl p-6 md:p-7 border border-gray-100 hover:border-brand-green-200 transition-all duration-300 hover:-translate-y-1 ${
                  isVisible["promise"]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section
        className="py-14 md:py-20 bg-soft-gray"
        data-animate-id="plans"
        ref={setRef("plans")}
      >
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("plans", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Compare Plans
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Find Your Perfect Care Plan
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              All plans include a free in-home assessment, personalized caregiver matching, and ongoing care coordination.
            </p>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto mt-2 font-medium">
              <i className="ri-arrow-right-circle-line mr-1 text-brand-green-600" />
              Contact us today for a customized care plan and pricing tailored to your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {plans.map((plan, i) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border ${
                  plan.highlight
                    ? "border-brand-green-300 shadow-lg"
                    : "border-gray-100 hover:border-brand-green-200"
                } overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                  isVisible["plans"]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {plan.highlight && (
                  <div className="absolute top-0 left-0 right-0 bg-brand-green-600 text-white text-center py-1.5 text-xs font-semibold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <div className={`p-6 md:p-8 ${plan.highlight ? "pt-10" : ""}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{plan.tagline}</p>
                  <p className="text-sm text-gray-500 leading-relaxed mb-6">
                    {plan.description}
                  </p>

                  <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-green-600 mb-4">
                    Services Include
                  </h4>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-check-line text-brand-green-600" />
                        </div>
                        {featureServiceMap[feature] ? (
                          <Link
                            to={`/services#${featureServiceMap[feature]}`}
                            className="text-sm text-gray-700 hover:text-brand-green-600 underline underline-offset-2 decoration-brand-green-300/60 hover:decoration-brand-green-600 transition-colors"
                          >
                            {feature}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-700">{feature}</span>
                        )}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className={`flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold text-sm transition-all duration-200 whitespace-nowrap ${
                      plan.highlight
                        ? "bg-brand-green-600 text-white hover:bg-brand-green-700"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                    }`}
                  >
                    <i className="ri-arrow-right-line" />
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section
        className="py-14 md:py-20 bg-white"
        data-animate-id="steps"
        ref={setRef("steps")}
      >
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className={animateClass("steps", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Getting Started
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Simple Steps to Get Started
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className={`relative bg-soft-gray rounded-xl p-6 border border-gray-100 transition-all duration-500 hover:border-brand-green-200 ${
                  isVisible["steps"]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <span className="text-3xl font-bold text-brand-green-200 mb-3 block">
                  {step.num}
                </span>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className={animateClass("steps", "mt-10 text-center")}>
            <p className="text-sm md:text-base text-gray-600 font-medium mb-5">
              <i className="ri-arrow-right-circle-line mr-1 text-brand-green-600" />
              Call now or request a consultation to receive your customized pricing.
            </p>
            <a
              href="tel:+17736997894"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-brand-green-600 text-white font-semibold rounded-full hover:bg-brand-green-700 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line" />
              Call 773-699-7894
            </a>
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section
        className="py-14 md:py-20 bg-soft-gray"
        data-animate-id="payment"
        ref={setRef("payment")}
      >
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("payment", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Payment Options
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Flexible Payment Solutions
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We provide flexible payment solutions and guidance to make quality care accessible for every family.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {billingOptions.map((option, i) => (
              <div
                key={option.title}
                className={`bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-brand-green-200 transition-all duration-300 hover:-translate-y-1 ${
                  isVisible["payment"]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${option.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-14 md:py-20 bg-white"
        data-animate-id="faq"
        ref={setRef("faq")}
      >
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          <div className={animateClass("faq", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Common Questions
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className={animateClass("faq", "space-y-3")}>
            {faqs.map((faq, idx) => (
              <div
                key={faq.q}
                className="bg-soft-gray rounded-xl border border-gray-100 overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-lg text-brand-green-600 transition-transform duration-300 ${
                        openFaq === idx ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === idx ? "max-h-40" : "max-h-0"
                  }`}
                >
                  <p className="text-sm text-gray-500 leading-relaxed px-5 md:px-6 pb-5 md:pb-6">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className={animateClass("faq", "mt-8 text-center")}>
            <p className="text-sm text-gray-500 mb-3">
              Have more questions? Browse our full FAQ for detailed answers about services, caregivers, and pricing.
            </p>
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-green-50 text-brand-green-700 text-sm font-semibold rounded-full hover:bg-brand-green-100 transition-all"
            >
              View Full FAQ
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Contact us today for a free, no-obligation care assessment. We will help you choose the right plan and get care started quickly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Schedule Free Assessment
            </Link>
            <a
              href="tel:+17736997894"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Call 773-699-7894 (24/7)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}